import { useAddRemoveCustomerTagMutation } from 'api/user-domain/user-domain';
import AddEditAttributesForm from 'components/forms/add-edit-attributes-form/add-edit-attributes-form.component';
import { useAddCustomerAttributesForm } from 'components/forms/add-edit-attributes-form/add-edit-attributes-form.hooks';
import { useCustomersTags } from 'hooks/customer-tags.hooks';
import { t } from 'i18next';
import FormDrawer from '../form-drawer/form-drawer.component';
import { IncDrawerType } from '../inc-drawer/inc-drawer.component';

export type CustomerEditAttributesDrawerDataType = { customerId?: string };

type CustomerEditAttributesDrawerProps =
  IncDrawerType<CustomerEditAttributesDrawerDataType>;

const CustomerEditAttributesDrawer = ({
  isOpen,
  close,
  data: drawerData,
}: CustomerEditAttributesDrawerProps) => {
  const { userSpecialTags, isLoading } = useCustomersTags(
    drawerData?.customerId,
  );
  const { form, customerAttributeFields } =
    useAddCustomerAttributesForm(userSpecialTags);

  const [addCustomerTagMutation, { isLoading: saveAttributesLoading }] =
    useAddRemoveCustomerTagMutation();

  const submit = async data => {
    const newAttributes = data.attributes.filter(
      attribute => !userSpecialTags.some(ct => ct.key === attribute.key),
    );
    const updatedAttributes = data.attributes.filter(attribute => {
      const originalTag = userSpecialTags.find(ct => ct.key === attribute.key);
      return !originalTag ? false : attribute.value !== originalTag?.value;
    });
    const add = [...newAttributes, ...updatedAttributes];
    const remove = userSpecialTags.filter(
      ct => !data.attributes.some(attribute => ct.key === attribute.key),
    );

    if (add.length || remove.length) {
      try {
        await addCustomerTagMutation({
          userId: drawerData?.customerId,
          body: {
            add: add.length
              ? JSON.stringify(
                  add.reduce((acc, a) => {
                    acc[a.key] = a.value;
                    return acc;
                  }, {}),
                )
              : null,
            remove: remove.length
              ? JSON.stringify(
                  remove.reduce((acc, a) => {
                    acc[a.key] = a.value;
                    return acc;
                  }, {}),
                )
              : null,
          },
        });
        close();
        form.reset();
      } catch (_e) {}
    } else {
      cancel();
    }
  };

  const cancel = () => {
    close();
    form.reset({ attributes: userSpecialTags });
  };

  return (
    <FormDrawer
      title={t('page.customers.form.attributes.addEditAttributes')}
      submitButtonLabel={t('page.customers.form.attributes.saveAttributes')}
      data-testid="AddAttributesDrawer.Drawer"
      open={isOpen}
      onClose={cancel}
      onValid={submit}
      cancel={cancel}
      formMethods={form}
      isLoading={saveAttributesLoading || isLoading}
    >
      <AddEditAttributesForm
        customerAttributeFields={customerAttributeFields}
      />
    </FormDrawer>
  );
};

export default CustomerEditAttributesDrawer;
